/**
 * The pieces the app slots into the Shell: environment switcher, workspace switcher, live
 * indicator, top-bar buttons, side panels, the notifications list and the user menu.
 * Presentation only — every one takes its data and callbacks as props. Design:
 * designs/app/Shell.dc.html (top bar and sidebar), hand-off README "Navigation".
 */
import type { ReactNode } from 'react';
import { Check, ChevronDown, ChevronsUpDown, LogOut, Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup,
  DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../primitives/dropdown-menu';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../primitives/sheet';
import { cn, formatNumber, formatRelativeTime } from '../utils';
import { StateBlock } from './StateBlock';
import { humanizeStatus } from './status-pill-map';
import type { ThemePreference } from './theme';

// ── Environment switcher ──────────────────────────────────────────────────────

export interface ShellEnvironment {
  readonly id: string;
  readonly name: string;
  /** dev | qa | uat | staging | production. Production always renders dark. */
  readonly class: string | null;
}

export function isProductionClass(cls: string | null | undefined): boolean {
  return cls === 'production';
}

export interface EnvironmentSwitcherProps {
  readonly environments: readonly ShellEnvironment[];
  /** The environment the server says this session is in. */
  readonly active: ShellEnvironment | null;
  readonly onSelect: (id: string) => void;
}

export function EnvironmentDot({ cls }: { cls: string | null | undefined }) {
  return <span className="ifx-env__dot" data-production={isProductionClass(cls) || undefined} aria-hidden="true" />;
}

export function EnvironmentSwitcher({ environments, active, onSelect }: EnvironmentSwitcherProps) {
  const prod = isProductionClass(active?.class);
  const name = active?.name ?? 'Default environment';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="ifx-env" data-production={prod || undefined} aria-label={`Environment: ${name}. Change environment`}>
        <EnvironmentDot cls={active?.class} />
        <span className="ifx-env__name">{name}</span>
        <ChevronDown className="ifx-env__chevron" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="ifx-menu">
        <DropdownMenuLabel>Environment</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={active?.id ?? ''} onValueChange={onSelect}>
          {environments.map((e) => (
            <DropdownMenuRadioItem key={e.id} value={e.id}>
              <EnvironmentDot cls={e.class} />
              <span className="ifx-menu__grow">{e.name}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Workspace switcher ────────────────────────────────────────────────────────

export interface ShellWorkspace {
  readonly id: string;
  readonly name: string;
  /** Second line under the name: the plan or the person's role, as the server names it. */
  readonly detail?: string | undefined;
}

export interface WorkspaceSwitcherProps {
  readonly workspaces: readonly ShellWorkspace[];
  readonly active: ShellWorkspace | null;
  readonly onSelect: (id: string) => void;
  /** Rail: the initial only. */
  readonly collapsed?: boolean | undefined;
  /** "Sign in to a specific workspace" — the workspace-ID box. */
  readonly onUseWorkspaceId?: (() => void) | undefined;
}

export function WorkspaceMark({ name }: { name: string }) {
  return <span className="ifx-workspace__mark" aria-hidden="true">{name.trim().charAt(0).toUpperCase() || '·'}</span>;
}

export function WorkspaceSwitcher({ workspaces, active, onSelect, collapsed = false, onUseWorkspaceId }: WorkspaceSwitcherProps) {
  const name = active?.name ?? 'Workspace';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="ifx-workspace" data-collapsed={collapsed || undefined} aria-label={`Workspace: ${name}. Switch workspace`}>
        <WorkspaceMark name={name} />
        {!collapsed && (
          <span className="ifx-workspace__text">
            <span className="ifx-workspace__name">{name}</span>
            {active?.detail && <span className="ifx-workspace__detail">{active.detail}</span>}
          </span>
        )}
        {!collapsed && <ChevronsUpDown className="ifx-workspace__chevron" aria-hidden="true" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="ifx-menu">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        {workspaces.map((w) => (
          <DropdownMenuItem key={w.id} onSelect={() => onSelect(w.id)}>
            <WorkspaceMark name={w.name} />
            <span className="ifx-menu__grow">{w.name}</span>
            {w.id === active?.id && <Check aria-label="Current workspace" />}
          </DropdownMenuItem>
        ))}
        {onUseWorkspaceId && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onUseWorkspaceId}>Sign in to a specific workspace</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Live indicator ────────────────────────────────────────────────────────────

export type LiveState = 'live' | 'connecting' | 'offline';

const LIVE_TEXT: Record<LiveState, string> = { live: 'Live', connecting: 'Connecting…', offline: 'Not live' };
const LIVE_TITLE: Record<LiveState, string> = {
  live: 'Updates arrive as they happen.',
  connecting: 'Connecting to live updates.',
  offline: 'Live updates are paused. Pages still refresh when you open them.',
};

export function LiveIndicator({ state }: { state: LiveState }) {
  return (
    <span className="ifx-live" data-state={state} role="status" title={LIVE_TITLE[state]}>
      <span className="ifx-live__dot" aria-hidden="true" />{LIVE_TEXT[state]}
    </span>
  );
}

// ── Top-bar buttons ───────────────────────────────────────────────────────────

export interface TopBarButtonProps {
  readonly icon: LucideIcon;
  /** Accessible name; shown as text only when `showLabel`. */
  readonly label: string;
  readonly showLabel?: boolean | undefined;
  /** Visible text beside the icon, e.g. "2 running". */
  readonly text?: string | undefined;
  /** Unread count, drawn as the brand badge. */
  readonly badge?: number | null | undefined;
  readonly expanded?: boolean | undefined;
  /** Opens a panel (default). False for a button that navigates instead. */
  readonly popup?: boolean | undefined;
  readonly onClick: () => void;
  readonly className?: string | undefined;
}

export function TopBarButton({ icon: Icon, label, showLabel, text, badge, expanded, popup = true, onClick, className }: TopBarButtonProps) {
  const name = badge ? `${label}, ${badge} unread` : label;
  return (
    <button
      type="button"
      className={cn('ifx-topbar__button', className)}
      onClick={onClick}
      aria-label={name}
      aria-haspopup={popup ? 'dialog' : undefined}
      aria-expanded={popup ? (expanded ?? false) : undefined}
    >
      <Icon aria-hidden="true" />
      {(showLabel || text) && <span aria-hidden="true">{text ?? label}</span>}
      {badge ? <span className="ifx-badge" aria-hidden="true">{badge > 99 ? '99+' : formatNumber(badge)}</span> : null}
    </button>
  );
}

export function TopBarDivider() {
  return <span className="ifx-topbar__divider" aria-hidden="true" />;
}

// ── Side panels ───────────────────────────────────────────────────────────────

export interface SidePanelProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string | undefined;
  /** Right of the title, e.g. "Mark all read". */
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  /** Full-screen sheet from the bottom on a phone. */
  readonly mobile?: boolean | undefined;
}

/** A non-blocking side panel (notifications, jobs, cloud shell, Ask Inferrex). */
export function SidePanel({ open, onOpenChange, title, description, actions, children, mobile = false }: SidePanelProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={mobile ? 'bottom' : 'right'} className={cn('ifx-panel', mobile && 'ifx-panel--mobile')}>
        <div className="ifx-panel__head">
          <SheetTitle className="ifx-panel__title">{title}</SheetTitle>
          {actions}
        </div>
        {description
          ? <SheetDescription className="ifx-panel__description">{description}</SheetDescription>
          : <SheetDescription className="ifx-sr-only">{title}</SheetDescription>}
        <div className="ifx-panel__body">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * The body of a panel whose feature is not built yet. A working entry point that says so —
 * never a fake list. `step` names when it arrives (hand-off README "Build order").
 */
export function LaterStepPanelBody({ what, step }: { what: string; step: string }) {
  return <StateBlock kind="empty" title="Coming in a later step" message={`${what} arrives in ${step}. Nothing here is live yet.`} />;
}

// ── Notifications ─────────────────────────────────────────────────────────────

export interface ShellNotification {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly createdAt: string;
  readonly read: boolean;
  /** In-app address it opens, or null when it points nowhere in the app. */
  readonly href: string | null;
}

export interface NotificationListProps {
  readonly items: readonly ShellNotification[];
  readonly onOpen: (item: ShellNotification) => void;
}

export function NotificationList({ items, onOpen }: NotificationListProps) {
  if (items.length === 0) {
    return <StateBlock kind="empty" title="No notifications" message="When something needs you, it will show here." />;
  }
  return (
    <ul className="ifx-notes" aria-label="Notifications">
      {items.map((n) => (
        <li key={n.id}>
          <button type="button" className="ifx-notes__item" data-unread={!n.read || undefined} onClick={() => onOpen(n)}>
            <span className="ifx-notes__dot" aria-hidden="true" />
            <span className="ifx-notes__text">
              <span className="ifx-notes__title">{n.title}{!n.read && <span className="ifx-sr-only"> (unread)</span>}</span>
              <span className="ifx-notes__body">{n.body}</span>
            </span>
            <time className="ifx-notes__time" dateTime={n.createdAt}>{formatRelativeTime(n.createdAt)}</time>
          </button>
        </li>
      ))}
    </ul>
  );
}

// ── User menu ─────────────────────────────────────────────────────────────────

export interface UserMenuProps {
  readonly name: string;
  readonly detail?: string | undefined;
  readonly collapsed?: boolean | undefined;
  readonly theme: ThemePreference;
  readonly onThemeChange: (theme: ThemePreference) => void;
  readonly onAccount?: (() => void) | undefined;
  readonly onSignOut: () => void;
}

export function initialsOf(name: string): string {
  const parts = name.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0]![0]}${parts[parts.length - 1]![0]}` : (parts[0] ?? '?').slice(0, 2);
  return letters.toUpperCase();
}

export function UserMenu({ name, detail, collapsed = false, theme, onThemeChange, onAccount, onSignOut }: UserMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="ifx-user" aria-label={`Account: ${name}`}>
        <span className="ifx-user__avatar" aria-hidden="true">{initialsOf(name)}</span>
        {!collapsed && (
          <span className="ifx-user__text">
            <span className="ifx-user__name">{name}</span>
            {detail && <span className="ifx-user__detail">{humanizeStatus(detail)}</span>}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="ifx-menu">
        <DropdownMenuLabel>{name}</DropdownMenuLabel>
        {onAccount && <DropdownMenuItem onSelect={onAccount}>Your account</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => onThemeChange(v as ThemePreference)}>
          <DropdownMenuRadioItem value="light"><Sun aria-hidden="true" />Light</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark"><Moon aria-hidden="true" />Dark</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system"><Monitor aria-hidden="true" />Match my device</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut}><LogOut aria-hidden="true" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
